'use client'

import { memo, useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Autoplay, Virtual } from 'swiper/modules'
import { Image } from '~/components/ui/image'
import { getImageUrlCrs } from '~/utils/url-image-crs'
import 'swiper/css'
import 'swiper/css/pagination'

interface RoomTypeBannerProps {
  images: string[]
  showSlider?: boolean
  onClick?: () => void
}

function RoomTypeBanner({ images, showSlider = false, onClick }: RoomTypeBannerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isInView, setIsInView] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  // IntersectionObserver để chỉ render Swiper khi component vào viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  if (!images || images.length === 0) return null
  if (!showSlider || images.length === 1) {
    return (
      <div ref={containerRef} className="relative h-full cursor-pointer" onClick={onClick}>
        {isInView && (
          <Image
            src={getImageUrlCrs(images[0], 'thumbnail')}
            alt="Room image"
            objectFit="fill"
            className="h-full w-full"
            fill
            loading="lazy"
          />
        )}
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative h-full">
      {isInView && (
        <Swiper
          modules={[Pagination, Autoplay, Virtual]}
          pagination={{ clickable: true }}
          loop={true}
          autoplay={false}
          virtual
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
          className="h-full w-full"
        >
          {images.map((image, index) => (
            <SwiperSlide key={index} virtualIndex={index}>
              <Image
                src={getImageUrlCrs(image, 'thumbnail')}
                alt={`Room image ${index + 1}`}
                objectFit="cover"
                className="h-full w-full"
                fill
                loading={index === activeIndex ? 'eager' : 'lazy'}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      <style jsx global>{`
        .swiper-pagination-bullet {
          width: 5px;
          height: 5px;
          background: #9ca1ab;
          opacity: 1;
        }
        .swiper-pagination-bullet-active {
          background: #fff;
        }
        .swiper-pagination {
          bottom: 16px !important;
          border-radius: 3px;
          background: rgba(0, 0, 0, 0.42);
          display: flex;
          gap: 0;
          padding: 2px 3px;
          align-items: center;
          justify-content: center;
          width: auto !important;
          left: 50% !important;
          transform: translateX(-50%);
        }
      `}</style>
    </div>
  )
}

function arePropsEqual(prevProps: RoomTypeBannerProps, nextProps: RoomTypeBannerProps) {
  return prevProps.images.every((image, index) => image === nextProps.images[index])
}

export default memo(RoomTypeBanner, arePropsEqual)
